import { Injectable } from '@angular/core';

import { HttpClientService } from '../common/http-client.service';

@Injectable()
export class DocumentService {
  /**
   * Base URL for the controller
   * @type {String}
   */
  private documentsURL = 'documents';

  /**
   * Constructor
   * @param {HttpClientService} private httpClient Service for making HTTP requests with different methods
   */
  constructor(private httpClient: HttpClientService) {}

  /**
   * Add author to document
   * @param  {any}          author      author object
   * @param  {number}       document_id Id of document to add author for
   * @return {Promise<any>}             Server response
   */
  addAuthor(author: any, document_id: number): Promise<any> {
    return this.httpClient.post(`${this.documentsURL}/add_author.json`, { author: author, id: document_id });
  }

  bulkOperation(params: any): Promise<any> {
    return this.httpClient.post(`${this.documentsURL}/bulk_operation.json`, params);
  }

  /**
   * Calculate word count for document
   * @param  {string}           html Document content HTML (used when document parts are not available separately)
   * @param  {Array<string> =    []}          htmlArray Document parts HTML (optional, used if document parts content are available separately)
   * @return {Promise<any>}          Server response
   */
  calculateWordCount(html: string, htmlArray: Array<string> = []): Promise<any> {
    return this.httpClient.post(`${this.documentsURL}/calculate_word_count.json`, { html: html, html_array: htmlArray });
  }

  /**
   * Cancel document edit
   * @param  {number}       document_id Id of document to cancel
   * @return {Promise<any>}             Server response
   */
  cancel(document_id: number): Promise<any> {
    return this.httpClient.post(`${this.documentsURL}/${document_id}/cancel.json`, {});
  }

  /**
   * Clone document
   * @param  {number}       matter_id    Id of matter to clone to
   * @param  {number}       document_id  Id of document to clone
   * @param  {any}          cloneOptions Clone options to select document associations to clone
   * @return {Promise<any>}              Server response
   */
  clone(matter_id: number, document_id: number, cloneOptions: any): Promise<any> {
    return this.httpClient.post(`/matters/${matter_id}/${this.documentsURL}/${document_id}/clone.json`, cloneOptions);
  }

  /**
   * Delete document
   * @param  {number}       document_id Id of document to delete
   * @return {Promise<any>}             Server response
   */
  deleteDocument(document_id: number): Promise<any> {
    return this.httpClient.delete(`${this.documentsURL}/${document_id}.json`);
  }

  /**
   * Remove author from document
   * @param  {number}       author_id Id of document author record to remove
   * @return {Promise<any>}           Server response
   */
  destroyAuthor(author_id: number): Promise<any> {
    return this.httpClient.get(`${this.documentsURL}/destroy_author.json`, { id: author_id });
  }

  /**
   * Get document extension from file name
   * @param  {string} file_name File name to get extension
   * @return {string}           Server response
   */
  extension(file_name: string): string {
    return file_name.slice(((file_name.lastIndexOf('.') - 1) >>> 0) + 2);
  }

  filterSelect(params: any): Promise<any> {
    return this.httpClient.get(`${this.documentsURL}/filter_select.json`, params);
  }

  /**
   * Get document authors for the document
   * @param  {number}       document_id Id of document to get document authors for
   * @return {Promise<any>}             Server response
   */
  getDocAuthors(document_id: number): Promise<any> {
    return this.httpClient.get(`${this.documentsURL}/get_doc_authors.json`, { id: document_id });
  }

  /**
   * Get available document authors for the matter of the document
   * @param  {number}       document_id Id of document used to get matter
   * @return {Promise<any>}             Server response
   */
  getDocumentAuthors(document_id: number): Promise<any> {
    return this.httpClient.get(`${this.documentsURL}/get_document_authors_available.json`, { id: document_id });
  }

  /**
   * Get document for edit
   * @param  {number}       document_id Id of document to edit
   * @param  {number}       matter_id   Matter id of the document
   * @return {Promise<any>}             Server response
   */
  getDocumentEdit(document_id: number, matter_id: number): Promise<any> {
    return this.httpClient.get(`${this.documentsURL}/${document_id}/edit.json`, { matter_id: matter_id });
  }

  /**
   * Get documents list
   * @param  {any}          search_params Search params for the documents list
   * @return {Promise<any>}               Server response
   */
  getDocuments(search_params: any): Promise<any> {
    search_params.get_documents = true;
    return this.httpClient.get(`${this.documentsURL}.json`, search_params);
  }

  ocrDocument(documentId: number): Promise<any> {
    return this.httpClient.post(`${this.documentsURL}/${documentId}/ocr.json`, {});
  }

  convertToWord(document: any): Promise<any> {
    const documentId = document && document.id ? document.id : document;
    const data = document && document.id ? { matter_id: document.matter_id, document_name: document.name } : {};
    return this.httpClient.post(`${this.documentsURL}/${documentId}/convert_to_word.json`, data);
  }

  proofTemplates(matter_id: number): Promise<Array<{ id: number; name: string; default: boolean }>> {
    return this.httpClient.get(`${this.documentsURL}/proof_templates.json`, { matter_id: matter_id });
  }

  /**
   * Get document for show tab
   * @param  {number}       document_id Id of document to show
   * @return {Promise<any>}             Server response
   */
  getDocumentShow(document_id: number): Promise<any> {
    return this.httpClient.get(`${this.documentsURL}/${document_id}.json`, { angular: '1' });
  }

  /**
   * Get form options for document form
   * @param  {any}          params Request params
   * @return {Promise<any>}        Server response
   */
  getFormOptions(params: any): Promise<any> {
    params.get_form_options = true;
    return this.httpClient.get(`${this.documentsURL}.json`, params);
  }

  /**
   * Get search options for matter page document list
   * @param  {number}       matter_id Id of matter for the document list
   * @return {Promise<any>}           Server response
   */
  getMatterSearchOptions(matter_id: number): Promise<any> {
    return this.httpClient.get(`${this.documentsURL}.json`, { get_matter_search_options: true, matter_id: matter_id });
  }

  /**
   * Get search options for documents index page
   * @return {Promise<any>} Server response
   */
  getSearchOptions(): Promise<any> {
    return this.httpClient.get(`${this.documentsURL}.json`, { get_search_options: true });
  }

  /**
   * Fetch contact for document form sidebar for letters
   * @param  {any}          involvement_id Id of involvement for the contact in the document's matter
   * @return {Promise<any>}                Server response
   */
  fetchContact(involvement_id: any): Promise<any> {
    return this.httpClient.get(`${this.documentsURL}/fetch_contact.json`, { involvement_id: involvement_id });
  }

  /**
   * Fetch selected template in the document form
   * @param  {any}          params Request params with template info
   * @return {Promise<any>}        Server response
   */
  fetchTemplate(params: any): Promise<any> {
    return this.httpClient.get(`${this.documentsURL}/fetch_template.json`, params);
  }

  /**
   * Rename document
   * @param  {number}       document_id Id of document to rename
   * @param  {string}       desire      Rename request type
   * @param  {string}       new_name    New name for the document
   * @return {Promise<any>}             Server response
   */
  rename(document_id: number, desire: string, new_name: string): Promise<any> {
    return this.httpClient.get(`${this.documentsURL}/rename.json`, { desire: desire, id: document_id, new_name: new_name });
  }

  /**
   * Save document
   * @param  {any}          data Document info including contacts for letters
   * @return {Promise<any>}      Server response
   */
  saveDocument(data: any): Promise<any> {
    if (data.id) return this.httpClient.put(`${this.documentsURL}/${data.id}.json`, data);
    else return this.httpClient.post(`${this.documentsURL}.json`, data);
  }

  /**
   * Save notes for document
   * @param  {string}       notes       Notes
   * @param  {number}       document_id Id of the document for the notes to be saved to
   * @return {Promise<any>}             Server response
   */
  saveNotes(notes: string, document_id: number): Promise<any> {
    return this.httpClient.put(`${this.documentsURL}/${document_id}.json`, { angular_2: '3', id: document_id, notes: notes });
  }

  /**
   * Set authored at date
   * @param  {string}       authored_at Date for authored at
   * @param  {number}       document_id Id of document to update authored at
   * @return {Promise<any>}             Server response
   */
  setAuthoredAt(authored_at: string, document_id: number): Promise<any> {
    return this.httpClient.put(`${this.documentsURL}/${document_id}.json`, { angular_2: '6', id: document_id, authored_at: authored_at });
  }

  /**
   * Set document type for document
   * @param  {any}          documentType Document type for the document
   * @param  {number}       document_id  Id of the document
   * @return {Promise<any>}              Server response
   */
  setDocumentType(documentType: any, document_id: number): Promise<any> {
    return this.httpClient.put(`${this.documentsURL}/${document_id}.json`, { angular_2: '5', id: document_id, doc_type: documentType });
  }

  /**
   * Set document version for the document
   * @param  {any}          version     Version of the document to be used
   * @param  {number}       document_id Id of document
   * @return {Promise<any>}             Server response
   */
  setDocumentVersion(version: any, document_id: number): Promise<any> {
    return this.httpClient.post(`${this.documentsURL}/${document_id}/set_document_version.json`, { id: document_id, version: version });
  }

  /**
   * Set document editability
   * @param  {boolean}      editable    Editability of the document
   * @param  {number}       document_id Id of document to update editability
   * @return {Promise<any>}             Server response
   */
  setEditability(editable: boolean, document_id: number): Promise<any> {
    return this.httpClient.put(`${this.documentsURL}/${document_id}.json`, { angular_2: '4', id: document_id, is_editable: editable });
  }

  /**
   * Set exhibit number for the document
   * @param  {string}       exhibitNumber Exhibit number for the document
   * @param  {number}       document_id   Id of document to set the exhibit number for
   * @return {Promise<any>}               Server response
   */
  setExhibitNumber(exhibitNumber: string, document_id: number): Promise<any> {
    return this.httpClient.put(`${this.documentsURL}/${document_id}.json`, { angular_2: '2', exhibitNumber: exhibitNumber });
  }

  /**
   * Word count for the document
   * @param  {number}       documentId Id of document to get the word count for
   * @return {Promise<any>}            Server response
   */
  wordCount(documentId: number): Promise<any> {
    return this.httpClient.post(`${this.documentsURL}/${documentId}/word_count.json`, {});
  }

  /**
   * Write document to PDF, used when document has document parts and PDF is missing
   * @param  {number}       id [description]
   * @return {Promise<any>}    [description]
   */
  writeToPdf(id: number): Promise<any> {
    return this.httpClient.put(`${this.documentsURL}/${id}.json`, { write_to_pdf: true });
  }
}
